import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Star, ChatCircleDots, PencilCircle, CaretUp, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import LazyAvatar from "@/components/LazyAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LiveStreamEmbed } from "@/components/LiveStreamEmbed";
import { ResourcesBlock } from "@/components/ResourcesBlock";
import { BlockPapersBlock } from "@/components/BlockPapersBlock";
import { isConferenceOver } from "@/lib/conference";

export default function SessionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [favorited, setFavorited] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [polls, setPolls] = useState([]);
  const [qText, setQText] = useState("");
  const [posting, setPosting] = useState(false);
  const [info, setInfo] = useState(null);
  const qTextareaRef = useRef(null);
  const location = useLocation();

  // When the URL hash is #ask (deep link from the Programme "Submit a question" CTA),
  // scroll the Q&A form into view and focus the input.
  useEffect(() => {
    if (location.hash !== "#ask" || !session || !qTextareaRef.current) return;
    const el = document.getElementById("ask");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // small delay so the smooth scroll doesn't fight the focus on mobile
      setTimeout(() => qTextareaRef.current?.focus(), 350);
    }
  }, [location.hash, session]);

  const loadAll = async () => {
    try {
      const [s, q, p, i] = await Promise.all([
        api.get(`/sessions/${id}`),
        api.get(`/sessions/${id}/questions`),
        api.get(`/sessions/${id}/polls`),
        api.get(`/info`),
      ]);
      setSession(s.data); setQuestions(q.data); setPolls(p.data); setInfo(i.data);
      if (user) {
        const favs = await api.get("/favorites");
        setFavorited(favs.data.includes(id));
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id, user]);

  const toggleFav = async () => {
    if (!user) return toast.info("Sign in to star this session.");
    if (favorited) { await api.delete(`/favorites/${id}`); setFavorited(false); }
    else { await api.post(`/favorites/${id}`); setFavorited(true); }
  };

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!user) return toast.info("Sign in to ask a question.");
    if (qText.trim().length < 2) return;
    setPosting(true);
    // Optimistic
    const optimistic = { id: `tmp-${Date.now()}`, text: qText, user_name: user.name, upvote_count: 0, user_has_upvoted: false, status: "approved", created_at: new Date().toISOString() };
    setQuestions((prev) => [optimistic, ...prev]);
    try {
      const { data } = await api.post(`/sessions/${id}/questions`, { text: qText });
      setQText("");
      setQuestions((prev) => [data, ...prev.filter((q) => q.id !== optimistic.id)]);
      toast.success("Question posted");
    } catch (err) {
      setQuestions((prev) => prev.filter((q) => q.id !== optimistic.id));
      toast.error(formatApiError(err));
    } finally { setPosting(false); }
  };

  const upvote = async (qid) => {
    if (!user) return toast.info("Sign in to upvote.");
    setQuestions((prev) => prev.map((q) => q.id === qid ? { ...q, user_has_upvoted: !q.user_has_upvoted, upvote_count: q.upvote_count + (q.user_has_upvoted ? -1 : 1) } : q));
    try { await api.post(`/questions/${qid}/upvote`); } catch (err) { toast.error(formatApiError(err)); loadAll(); }
  };

  const votePoll = async (poll, answer) => {
    if (!user) return toast.info("Sign in to vote.");
    try {
      await api.post(`/polls/${poll.id}/vote`, { answer });
      const p = await api.get(`/sessions/${id}/polls`);
      setPolls(p.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const isAdmin = user?.role === "admin";

  const toggleResource = async (resourceId) => {
    try {
      const { data } = await api.post(`/sessions/${id}/resources/${resourceId}/toggle`);
      const wasReleased = (session?.resources_released || []).includes(resourceId);
      setSession((s) => (s ? { ...s, resources_released: data.resources_released, resources_items: (s.resources_items || []).map((it) => ({ ...it, released: data.resources_released.includes(it.id) })) } : s));
      if (!wasReleased) {
        // Item just transitioned to released - backend has fired the push.
        const n = data.push_recipients || 0;
        toast.success(n ? `Released - push sent to ${n} subscriber${n === 1 ? "" : "s"}` : "Released (no push subscribers yet)");
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const releaseAllResources = async () => {
    try {
      const { data } = await api.post(`/sessions/${id}/resources/release-all`);
      setSession((s) => (s ? { ...s, resources_released: data.resources_released, resources_items: (s.resources_items || []).map((it) => ({ ...it, released: true })) } : s));
      const n = data.push_recipients || 0;
      const newly = data.newly_released || 0;
      if (newly === 0) toast.success("All papers already released");
      else toast.success(n ? `${newly} paper${newly === 1 ? "" : "s"} released - push sent to ${n}` : `${newly} paper${newly === 1 ? "" : "s"} released (no push subscribers yet)`);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const hideAllResources = async () => {
    try {
      await api.post(`/sessions/${id}/resources/hide-all`);
      setSession((s) => (s ? { ...s, resources_released: [], resources_items: (s.resources_items || []).map((it) => ({ ...it, released: false })) } : s));
      toast.success("All papers hidden");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!session) return <div className="py-10 text-center text-[#7A828C]">Loading…</div>;

  // Q&A is restricted to the "Ask The Experts" / Featured Q&A session set in
  // Admin → Info. On every other session the Q&A tab is hidden so delegates
  // pool questions in one place. Once the conference is over we hide the tab
  // everywhere so the form does not invite stale submissions.
  const featuredQaId = info?.featured_qa_session_id || "";
  const qaEnabled = !isConferenceOver() && !!featuredQaId && (
    featuredQaId === session.id ||
    (session.block_session_ids || []).includes(featuredQaId)
  );
  // Polls feature is off for DFTB26 (admin can re-enable here if needed).
  const POLLS_ENABLED = false;
  const pollsVisible = POLLS_ENABLED && polls.length > 0;

  // Resolve live stream - Plenary and Concurrent 1 share a feed.
  const streamKey = session.stage === "Concurrent 1" ? "Plenary" : session.stage;
  const streamUrl = info?.live_streams?.[streamKey] || "";
  const showStream = isWithinLiveWindow(session) && streamUrl;

  return (
    <div data-testid="session-detail-page" className="space-y-6">
      <Link to="/program" className="inline-flex items-center gap-1 text-sm text-[#5a5a5a] hover:text-[#000000]" data-testid="back-to-program">
        <ArrowLeft size={16} /> Programme
      </Link>

      {showStream && (
        <LiveStreamEmbed url={streamUrl} label={session.stage_label || session.stage} testid="session-live-stream" />
      )}

      <div>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="font-mono-time text-xs text-[#000000]">{session.start_time}-{session.end_time}</span>
          <span className="font-mono-time text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#E5E9F0] rounded-sm">{session.stage_label || session.stage}</span>
          {session.track && <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">{session.track}</span>}
        </div>
        <h1 className="font-display text-2xl sm:text-3xl leading-tight mb-3">{session.title}</h1>
        {session.chair && (
          <div className="text-sm text-[#7A828C] mb-2" data-testid="session-chair">
            Chaired by <span className="text-[#1c1c24]">{session.chair}</span>
          </div>
        )}
        {session.description && <p className="text-[#5a5a5a] leading-relaxed">{session.description}</p>}

        <button onClick={toggleFav} data-testid="detail-favorite-btn" className="mt-4 inline-flex items-center gap-2 h-11 px-4 rounded-sm border border-[#E5E9F0] hover:border-[#000000] transition-colors">
          <Star size={18} weight={favorited ? "fill" : "regular"} className={favorited ? "text-[#000000]" : "text-[#5a5a5a]"} />
          <span className="text-sm">{favorited ? "In My Agenda" : "Add to My Agenda"}</span>
        </button>
      </div>

      {(session.speakers || []).length > 0 && (
        <div>
          <h2 className="font-mono-time text-xs uppercase tracking-widest text-[#7A828C] mb-3">Speakers</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {session.speakers.map((sp) => (
              <Link key={sp.id} to={`/speakers/${sp.id}`} data-testid={`detail-speaker-${sp.id}`}
                className="flex items-center gap-3 p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm hover:border-[#000000] transition-colors">
                <LazyAvatar
                  endpoint={`/speakers/${sp.id}/photo`}
                  hasPhoto={sp.has_photo}
                  name={sp.name}
                  className="w-12 h-12 rounded-sm flex-shrink-0"
                  round={false}
                />
                <div className="min-w-0">
                  <div className="font-medium truncate">{sp.name}</div>
                  <div className="text-xs text-[#7A828C] truncate">{sp.affiliation}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <ResourcesBlock
        items={session.resources_items}
        resources={session.resources}
        isAdmin={isAdmin}
        onToggle={toggleResource}
        onReleaseAll={releaseAllResources}
        onHideAll={hideAllResources}
      />
      <BlockPapersBlock sessionId={session.id} isAdmin={isAdmin} />

      {(qaEnabled || pollsVisible) && (
      <Tabs defaultValue={qaEnabled ? "qa" : "polls"} data-testid="session-interaction-tabs">
        <TabsList className={`bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm h-auto p-1 w-full grid ${qaEnabled && pollsVisible ? "grid-cols-2" : "grid-cols-1"}`}>
          {qaEnabled && (
            <TabsTrigger value="qa" className="rounded-sm py-2.5 data-[state=active]:bg-[#000000] data-[state=active]:text-[#FFFFFF] font-medium" data-testid="tab-qa">
              <ChatCircleDots size={16} className="mr-2" /> Q&A
            </TabsTrigger>
          )}
          {pollsVisible && (
            <TabsTrigger value="polls" className="rounded-sm py-2.5 data-[state=active]:bg-[#000000] data-[state=active]:text-[#FFFFFF] font-medium" data-testid="tab-polls">
              <PencilCircle size={16} className="mr-2" /> Polls
            </TabsTrigger>
          )}
        </TabsList>

        {qaEnabled && (
        <TabsContent value="qa" className="mt-4 space-y-3">
          <form id="ask" onSubmit={submitQuestion} className="space-y-2 scroll-mt-20" data-testid="qa-form">
            <Textarea
              ref={qTextareaRef}
              value={qText} onChange={(e) => setQText(e.target.value)}
              placeholder={user ? "Ask a question…" : "Sign in to ask a question"}
              disabled={!user} rows={2} className="rounded-sm bg-[#FFFFFF] border-[#E5E9F0]" data-testid="qa-input"
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={posting || !user || qText.trim().length < 2} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="qa-submit-btn">
                {posting ? "Posting…" : "Ask"}
              </Button>
            </div>
          </form>

          {questions.length === 0 ? (
            <div className="text-center py-8 text-[#7A828C] text-sm">No questions yet, be the first.</div>
          ) : (
            <div className="space-y-2">
              {questions.map((q) => (
                <div key={q.id} data-testid={`qa-item-${q.id}`} className={`p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex gap-3 ${q.status === "answered" ? "opacity-80" : ""}`}>
                  <button onClick={() => upvote(q.id)} className={`flex flex-col items-center justify-center w-11 min-h-[44px] rounded-sm border transition-colors ${q.user_has_upvoted ? "border-[#000000] text-[#000000]" : "border-[#E5E9F0] text-[#5a5a5a] hover:border-[#000000]"}`} data-testid={`qa-upvote-${q.id}`} aria-label="Upvote">
                    <CaretUp size={16} weight={q.user_has_upvoted ? "fill" : "regular"} />
                    <span className="text-xs font-mono-time">{q.upvote_count}</span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{q.text}</p>
                    <div className="text-xs text-[#7A828C] mt-1 flex items-center gap-2">
                      <span>{q.user_name}</span>
                      {q.status === "answered" && <span className="inline-flex items-center gap-1 text-[#000000]"><CheckCircle size={12} /> Answered</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        )}

        {pollsVisible && (
        <TabsContent value="polls" className="mt-4 space-y-4">
          {polls.map((poll) => (
            <PollCard key={poll.id} poll={poll} onVote={(ans) => votePoll(poll, ans)} />
          ))}
        </TabsContent>
        )}
      </Tabs>
      )}
    </div>
  );
}

function PollCard({ poll, onVote }) {
  const [wcInput, setWcInput] = useState("");
  const total = poll.total_responses || 0;
  return (
    <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid={`poll-${poll.id}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-display text-lg">{poll.question}</h3>
        <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">{poll.is_open ? "Open" : "Closed"}</span>
      </div>

      {poll.type === "multi" ? (
        <div className="space-y-2">
          {poll.options.map((opt) => {
            const count = poll.results?.[opt] || 0;
            const pct = total ? Math.round((count / total) * 100) : 0;
            const selected = poll.user_answer === opt;
            return (
              <button key={opt} onClick={() => onVote(opt)} disabled={!poll.is_open}
                className={`w-full text-left relative overflow-hidden rounded-sm border min-h-[48px] px-3 py-2 transition-colors ${selected ? "border-[#000000]" : "border-[#E5E9F0] hover:border-[#000000]"}`}
                data-testid={`poll-option-${opt}`}>
                <div className="absolute inset-y-0 left-0 bg-[#000000]/15" style={{ width: `${pct}%` }} />
                <div className="relative flex items-center justify-between">
                  <span className="text-sm">{opt}{selected && " ✓"}</span>
                  <span className="text-xs font-mono-time text-[#5a5a5a]">{pct}% · {count}</span>
                </div>
              </button>
            );
          })}
          <div className="text-xs text-[#7A828C] mt-2">{total} response{total === 1 ? "" : "s"}</div>
        </div>
      ) : (
        <>
          {poll.is_open && (
            <form onSubmit={(e) => { e.preventDefault(); if (wcInput.trim()) { onVote(wcInput.trim()); setWcInput(""); } }} className="flex gap-2 mb-3">
              <input value={wcInput} onChange={(e) => setWcInput(e.target.value)} maxLength={30} placeholder="One word…"
                className="flex-1 h-11 px-3 rounded-sm bg-[#FFFFFF] border border-[#E5E9F0] focus:border-[#000000] outline-none text-sm" data-testid={`poll-wc-input-${poll.id}`} />
              <Button type="submit" className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid={`poll-wc-submit-${poll.id}`}>Send</Button>
            </form>
          )}
          <WordCloud data={poll.results || {}} />
        </>
      )}
    </div>
  );
}

function WordCloud({ data }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <div className="text-sm text-[#7A828C] text-center py-6">No responses yet</div>;
  const max = entries[0][1];
  const bucket = (c) => {
    const r = c / max;
    if (r > 0.8) return "wc-4";
    if (r > 0.6) return "wc-3";
    if (r > 0.4) return "wc-2";
    if (r > 0.2) return "wc-1";
    return "wc-0";
  };
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 py-4 justify-center items-baseline">
      {entries.map(([w, c]) => (
        <span key={w} className={`${bucket(c)} font-display tracking-tight`} title={`${c}`}>{w}</span>
      ))}
    </div>
  );
}


function isWithinLiveWindow(session) {
  // Show the embedded stream from 15 min before start until 15 min after end.
  // Anything outside that window is treated as "not live" so we don't surface an
  // empty Vimeo Event lobby on yesterday's or tomorrow's sessions.
  if (!session?.date || !session?.start_time || !session?.end_time) return false;
  const now = new Date();
  if (now.toISOString().slice(0, 10) !== session.date) return false;
  const toMin = (t) => {
    const [h, m] = (t || "0:0").split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const nowMin = now.getHours() * 60 + now.getMinutes();
  return nowMin >= toMin(session.start_time) - 15 && nowMin <= toMin(session.end_time) + 15;
}
